'use client'

import { envWeb } from '@/lib/env-web'
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  loggerLink,
} from '@repo/trpc/trpc-client'
import { createTRPCContext } from '@repo/trpc/trpc-ts-react-query'
import type { QueryClient } from '@tanstack/react-query'
import { QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import SuperJSON from 'superjson'

import { createQueryClient } from '@/trpc/query-client'
import type { AppRouter } from '@repo/trpc'

let clientQueryClientSingleton: QueryClient | undefined = undefined
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return createQueryClient()
  } else {
    // Browser: use singleton pattern to keep the same query client.
    return (clientQueryClientSingleton ??= createQueryClient())
  }
}

export const { useTRPC, TRPCProvider } = createTRPCContext<AppRouter>()

// why this? for use in functions that are not react components, like excel generation
// export const trpcProxyClient = createTRPCProxyClient<AppRouter>({
export const trpcProxyClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: 'http://localhost:3000/api/trpc',
      transformer: SuperJSON,
      fetch(url, options) {
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore - this is how the official docs suggest to do it
        return fetch(url, {
          ...options,
          // https://trpc.io/docs/client/cors (send cookies along)
          credentials: 'include',
        })
      },
      // // You can pass any HTTP headers you wish here
      // async headers() {
      //   return {
      //     authorization: getAuthCookie(),
      //   };
      // },
    }),
  ],
})

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient()

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        loggerLink({
          enabled: (op) =>
            // TODO
            process.env.NODE_ENV === 'development' ||
            (op.direction === 'down' && op.result instanceof Error),
        }),
        httpBatchStreamLink({
          fetch(url, options) {
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            // @ts-ignore - this is how the official docs suggest to do it
            return fetch(url, {
              ...options,
              // https://trpc.io/docs/client/cors (send cookies along)
              credentials: 'include',
            })
          },
          transformer: SuperJSON,
          url: getBaseUrl() + '/api/trpc',
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          headers() {
            const headers = new Headers()
            headers.set('x-trpc-source', 'nextjs-react')
            // Add X-Business-Id header for super admin organization switching
            if (typeof window !== 'undefined') {
              const selectedBusinessId = localStorage.getItem('super_admin_selected_business_id')
              if (selectedBusinessId) {
                headers.set('x-business-id', selectedBusinessId)
              }
            }
            return headers
          },
        }),
      ],
    }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
      </TRPCProvider>
    </QueryClientProvider>
  )
}

const getBaseUrl = () => {
  console.log('inside getBaseUrl')
  console.log('getBaseUrl envWeb.MODE: ', envWeb.MODE)
  // if (typeof window !== 'undefined') return window.location.origin
  // if (envWeb.VITE_PRODUCTION_URL) return envWeb.VITE_PRODUCTION_URL
  // return `http://localhost:${process.env.PORT ?? 3000}`;
  // return `http://localhost:3000`
  console.log({
    VITE_BACKEND_URL: envWeb.VITE_BACKEND_URL,
    VITE_PRODUCTION_URL: envWeb.VITE_PRODUCTION_URL,
  })

  // In dev, use relative URL so requests go through Vite proxy (same-origin). This ensures
  // cookies set by devBypassLogin (and other auth) are stored for the page origin and
  // sent on subsequent requests. Direct localhost:4000 would be cross-origin and cookies
  // might not be sent correctly.
  const url = envWeb.MODE === 'production' ? envWeb.VITE_BACKEND_URL : ''
  // const url = <insert tailscale url here if working with tailscale/exposing local dev>
  console.log('getBaseUrl url: ', url || '(relative - using proxy)')
  return url
}
